import { TypedDocumentNode, gql } from '@apollo/client';

import { ReferralState, Sport } from '__generated__/globalTypes';
import { ReadyToClaimReferralBar } from 'components/referral/ReadyToClaimReferralBar';
import { useReferralRewards } from 'hooks/referral/useReferralRewards';
import useToggle from 'hooks/useToggle';

import { InProgressReferralBar } from './InProgressReferralBar';
import { ReferralBar_currentUser } from './__generated__/index.graphql';

type OuterProps = {
  sport: Sport;
};

type Props = OuterProps & {
  currentUser: ReferralBar_currentUser;
};

export const ReferralBar = ({ currentUser, sport }: Props) => {
  const { referralAsReferee, refereeRewards } = currentUser;
  const { isClaimed } = useReferralRewards(refereeRewards);
  const [openClaimDialog, toggleOpenClaimDialog] = useToggle(false);

  const claimed =
    refereeRewards.length > 0 &&
    refereeRewards.every(reward => isClaimed(reward));
  const notAReferee =
    !referralAsReferee ||
    referralAsReferee.state === ReferralState.EXPIRED ||
    (claimed && !openClaimDialog);

  if (notAReferee) {
    return null;
  }

  if ((!claimed || openClaimDialog) && refereeRewards.length > 0) {
    return (
      <ReadyToClaimReferralBar
        openClaimDialog={openClaimDialog}
        toggleOpenClaimDialog={toggleOpenClaimDialog}
        refereeRewards={refereeRewards}
      />
    );
  }

  return <InProgressReferralBar referee={referralAsReferee} sport={sport} />;
};

ReferralBar.fragments = {
  currentUser: gql`
    fragment ReferralBar_currentUser on CurrentUser {
      slug
      referee
      refereeRewards {
        id
        shippingState
        ...ReadyToClaimReferralBar_ReferralReward
        ...useReferralRewards_referralReward
      }
      referralAsReferee {
        id
        state
        ...InProgressReferralBar_Referral
      }
    }
    ${ReadyToClaimReferralBar.fragments.referralReward}
    ${useReferralRewards.fragments.referralReward}
    ${InProgressReferralBar.fragments.referral}
  ` as TypedDocumentNode<ReferralBar_currentUser>,
};
